import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircle, Clock, AlertCircle, ChevronDown, ChevronRight, Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import MetricTable     from '../components/Scorecard/MetricTable';
import OverallComments from '../components/Scorecard/OverallComments';
import NotesThread     from '../components/Scorecard/NotesThread';
import KPIBar          from '../components/Scorecard/KPIBar';
import StatusBadge     from '../components/Shared/StatusBadge';
import LoadingSpinner  from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';

const EVP_THEME     = { bg: '#EFF6FF', border: '#93C5FD', label: '#1D4ED8', sub: '#3B82F6', text: '#1E3A5F' };
const FINANCE_THEME = { bg: '#FEF3C7', border: '#FCD34D', label: '#92400E', sub: '#B45309', text: '#451A03' };

export default function ReviewDashboard() {
  const { costCenterId: paramCCId, periodId: paramPeriodId } = useParams();
  const { costCenters, isAdmin, isFinanceReviewer } = useAuth();
  const qc = useQueryClient();

  const [selectedCCId,     setSelectedCCId]     = useState(paramCCId || costCenters[0]?.id || null);
  // The sidebar's yard links end in /latest, which is not a period id: sent
  // as-is it reached a UUID column and the page could only fail. Treat it as
  // "no period chosen" so the default below picks the month.
  const [selectedPeriodId, setSelectedPeriodId] = useState(
    paramPeriodId && paramPeriodId !== 'latest' ? paramPeriodId : null);
  const [collapsed,        setCollapsed]         = useState({});

  useEffect(() => {
    if (!selectedCCId && costCenters.length) setSelectedCCId(costCenters[0].id);
  }, [costCenters, selectedCCId]);

  const periodsQuery = useQuery({
    queryKey: ['periods'],
    queryFn:  () => api.get('/periods'),
  });
  const periods = periodsQuery.data || [];

  useEffect(() => {
    if (selectedPeriodId || !periods.length) return;
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevYear  = prev.getFullYear();
    const prevMonth = prev.getMonth() + 1;
    const target =
      periods.find((p) => p.period_year === prevYear && p.period_month === prevMonth) ??
      periods[0];
    setSelectedPeriodId(target.id);
  }, [periods, selectedPeriodId]);

  const reviewQuery = useQuery({
    queryKey: ['review', selectedCCId, selectedPeriodId],
    queryFn:  () => api.get(`/reviews/${selectedCCId}/${selectedPeriodId}`),
    enabled:  !!selectedCCId && !!selectedPeriodId,
  });

  const { review, sections, access, currentUserId } = reviewQuery.data || {};
  const isLocked = review?.status === 'submitted';

  const commentMutation = useMutation({
    mutationFn: ({ metricValueId, comment }) =>
      api.put(`/metrics/${metricValueId}/comment`, { comment }),
    onError: () => toast.error('Failed to save comment.'),
  });

  const handleCommentChange = useCallback((metricValueId, comment) => {
    commentMutation.mutate({ metricValueId, comment });
  }, [commentMutation]);

  const overallMutation = useMutation({
    mutationFn: (comment) =>
      api.patch(`/reviews/${review?.id}/overall-comment`, { comment }),
    onSuccess: () => qc.invalidateQueries(['review', selectedCCId, selectedPeriodId]),
    onError:   () => toast.error('Failed to save overall comment.'),
  });

  const evpMutation = useMutation({
    mutationFn: (comment) =>
      api.put(`/reviews/${review?.id}/notes/evp`, { comment }),
    onSuccess: () => qc.invalidateQueries(['review', selectedCCId, selectedPeriodId]),
    onError:   () => toast.error('Failed to save EVP note.'),
  });

  const financeMutation = useMutation({
    mutationFn: (comment) =>
      api.put(`/reviews/${review?.id}/notes/finance`, { comment }),
    onSuccess: () => qc.invalidateQueries(['review', selectedCCId, selectedPeriodId]),
    onError:   () => toast.error('Failed to save finance note.'),
  });

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/reviews/${review?.id}/submit`),
    onSuccess: () => {
      toast.success('Review submitted successfully!');
      qc.invalidateQueries(['review', selectedCCId, selectedPeriodId]);
    },
    onError: (err) => {
      const list = err?.data?.missingMetrics;
      if (list && list.length) {
        toast.error(`Add a comment on: ${list.join(', ')}`, { duration: 6000 });
      } else {
        toast.error(err.message || 'Submission failed.');
      }
    },
  });

  function handleSubmit() {
    if (!window.confirm('Submit this review? It will be locked from further edits.')) return;
    submitMutation.mutate();
  }

  function toggleSection(section) {
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  const allMetrics = sections ? Object.values(sections).flat() : [];
  const kpi = {
    good:      allMetrics.filter((m) => m.status === 'good').length,
    risk:      allMetrics.filter((m) => m.status === 'risk').length,
    off:       allMetrics.filter((m) => m.status === 'off').length,
    total:     allMetrics.length,
    commented: allMetrics.filter((m) => m.userComment || m.allComments?.length).length,
  };

  const offWithoutMyComment = allMetrics.filter(
    (m) => m.status === 'off' && !(m.userComment && m.userComment.trim())
  );

  if (reviewQuery.isLoading || periodsQuery.isLoading) return <LoadingSpinner />;
  if (reviewQuery.isError) return <ErrorState message={reviewQuery.error?.message} />;

  return (
    <>
      <style>{`
        @media print {
          body { background: #fff !important; }
          #sidebar, .no-print { display: none !important; }
          table { page-break-inside: avoid; }
          .section-block { page-break-inside: avoid; margin-bottom: 16px !important; }
          .print-header { display: block !important; }
        }
        .print-header { display: none; }
      `}</style>

      <div style={s.page}>
        {/* Print-only header */}
        <div className="print-header" style={{ marginBottom: 16, borderBottom: '2px solid #1B2A4A', paddingBottom: 10 }}>
          <div style={{ fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: '#9CA3AF' }}>Athens Services</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1B2A4A' }}>
            Monthly Operations Review — {review?.costCenterName} — {review?.periodLabel}
          </div>
        </div>

        {/* Page header */}
        <PageHeader
          eyebrow="Athens Services"
          title="Monthly Operations Review"
          alignActions="end"
          meta={review && (
            <>
              <StatusBadge status={review.status} />
              <span style={s.dataSource}>
                {review.dataSource === 'ftp' ? '⟳ FTP Integration' : '↑ Manual Upload'}
              </span>
            </>
          )}
          actions={
            <>
              {(isAdmin || isFinanceReviewer) && (
                <select
                  style={s.select}
                  value={selectedCCId || ''}
                  onChange={(e) => setSelectedCCId(e.target.value)}
                >
                  {costCenters.map((cc) => (
                    <option key={cc.id} value={cc.id}>{cc.name}</option>
                  ))}
                </select>
              )}
              <select
                style={s.select}
                value={selectedPeriodId || ''}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
              >
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              {review && review.status !== 'pending' && (
                <button style={s.printBtn} onClick={() => window.print()} className="no-print">
                  <Printer size={14} />
                  Print / PDF
                </button>
              )}
            </>
          }
        />

        {review && <KPIBar kpi={kpi} isLocked={isLocked} />}

        {review && review.status === 'pending' && (
          <div style={s.noData}>
            <Clock size={28} color="#9CA3AF" />
            <div style={s.noDataTitle}>Awaiting Data</div>
            <div style={s.noDataSub}>
              Data for this period has not been loaded yet.
              It will appear automatically when the FTP file is processed,
              or an admin can upload it manually.
            </div>
          </div>
        )}

        {sections && Object.entries(sections).map(([section, metrics]) => (
          <div key={section} style={s.sectionBlock} className="section-block">
            <div style={s.sectionHd} onClick={() => toggleSection(section)}>
              <div style={s.sectionLeft}>
                <span style={s.chevron}>
                  {collapsed[section] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </span>
                <span style={s.sectionName}>{section}</span>
                <span style={s.sectionBadge}>
                  {metrics.filter((m) => m.status === 'off').length > 0 && (
                    <span style={{ ...s.badge, ...s.badgeOff }}>
                      {metrics.filter((m) => m.status === 'off').length} off track
                    </span>
                  )}
                  {metrics.filter((m) => m.status === 'risk').length > 0 && (
                    <span style={{ ...s.badge, ...s.badgeRisk }}>
                      {metrics.filter((m) => m.status === 'risk').length} at risk
                    </span>
                  )}
                </span>
              </div>
            </div>
            {!collapsed[section] && (
              <MetricTable
                metrics={metrics}
                isLocked={isLocked}
                canComment={access?.can_comment === true}
                onCommentChange={handleCommentChange}
              />
            )}
          </div>
        ))}

        {review && review.status !== 'pending' && (
          <OverallComments
            value={review.overallComment || ''}
            isLocked={isLocked || isAdmin || isFinanceReviewer || !access?.can_comment}
            onChange={(comment) => overallMutation.mutate(comment)}
          />
        )}

        {review && review.status !== 'pending' && isAdmin && (
          <NotesThread
            notes={review.evpNotes || []}
            currentUserId={currentUserId}
            canEdit={isAdmin}
            onSave={(comment) => evpMutation.mutate(comment)}
            theme={EVP_THEME}
            title="EVP Notes"
            description="Notes from each EVP/admin reviewing this cost center. DVPs cannot see this section."
            placeholder="Add your EVP notes — action items, flags, follow-ups…"
            emptyText="No EVP notes yet."
          />
        )}

        {review && review.status !== 'pending' && (
          <NotesThread
            notes={review.financeNotes || []}
            currentUserId={currentUserId}
            canEdit={isAdmin || isFinanceReviewer}
            onSave={(comment) => financeMutation.mutate(comment)}
            theme={FINANCE_THEME}
            title="Finance Notes"
            description="Finance team commentary on this cost center review. Visible to DVPs, admins, and finance."
            placeholder="Add finance notes — variance drivers, accounting context, follow-ups…"
            emptyText="No finance notes have been added."
          />
        )}

        {review && review.status !== 'submitted' && !isAdmin && !isFinanceReviewer && access?.can_submit && (
          <div style={s.footer} className="no-print">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={s.footerNote}>
                {kpi.commented} of {kpi.total} metrics commented
              </span>
              {offWithoutMyComment.length > 0 && (
                <span style={s.footerWarn}>
                  ⚠ {offWithoutMyComment.length} off-track metric{offWithoutMyComment.length === 1 ? '' : 's'} need your comment before you can submit
                </span>
              )}
            </div>
            <button
              style={{ ...s.submitBtn, opacity: offWithoutMyComment.length > 0 ? 0.5 : 1 }}
              onClick={handleSubmit}
              disabled={submitMutation.isPending || offWithoutMyComment.length > 0}
              title={offWithoutMyComment.length > 0
                ? `Add comments to: ${offWithoutMyComment.map((m) => m.metricName).join(', ')}`
                : undefined}
            >
              {submitMutation.isPending ? 'Submitting…' : 'Submit for Review →'}
            </button>
          </div>
        )}

        {review?.status === 'submitted' && (
          <div style={s.lockedBanner}>
            <CheckCircle size={16} />
            <span>Submitted {new Date(review.submittedAt).toLocaleDateString()}</span>
          </div>
        )}
      </div>
    </>
  );
}

function ErrorState({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <AlertCircle size={32} color="#DC2626" style={{ marginBottom: 12 }} />
      <div style={{ fontSize: 14, color: '#DC2626' }}>{message || 'Failed to load review.'}</div>
    </div>
  );
}

const s = {
  page:           { maxWidth: '1100px', margin: '0 auto' },
  dataSource:     { fontSize: '11px', color: '#9CA3AF' },
  select: {
    padding: '7px 10px', fontSize: '12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', background: '#fff', color: '#374151',
    fontFamily: 'inherit', cursor: 'pointer',
  },
  printBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '7px 13px', fontSize: '12px', fontWeight: 500,
    border: '1px solid #D1D5DB', borderRadius: '6px',
    background: '#fff', color: '#374151', cursor: 'pointer', fontFamily: 'inherit',
  },
  sectionBlock:  { marginBottom: '8px' },
  sectionHd: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '9px 14px', background: '#F1F5F9', border: '1px solid #E2E8F0',
    borderRadius: '6px', cursor: 'pointer', userSelect: 'none',
  },
  sectionLeft:   { display: 'flex', alignItems: 'center', gap: '8px' },
  chevron:       { color: '#9CA3AF', display: 'flex' },
  sectionName:   { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#374151' },
  sectionBadge:  { display: 'flex', gap: '6px' },
  badge:         { fontSize: '10px', padding: '2px 7px', borderRadius: '10px', fontWeight: 600 },
  badgeOff:      { background: '#FEE2E2', color: '#B91C1C' },
  badgeRisk:     { background: '#FEF9C3', color: '#A16207' },
  noData: {
    textAlign: 'center', padding: '60px 20px', background: '#fff',
    border: '1px solid #E5E7EB', borderRadius: '10px', marginBottom: '16px',
  },
  noDataTitle:   { fontSize: '16px', fontWeight: 600, color: '#374151', margin: '12px 0 6px' },
  noDataSub:     { fontSize: '13px', color: '#9CA3AF', maxWidth: '400px', margin: '0 auto', lineHeight: 1.6 },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: '20px', padding: '14px 0', borderTop: '1px solid #E5E7EB',
  },
  footerNote:    { fontSize: '12px', color: '#9CA3AF' },
  footerWarn:    { fontSize: '12px', color: '#B91C1C', fontWeight: 500 },
  submitBtn: {
    padding: '9px 20px', fontSize: '13px', fontWeight: 600, border: 'none',
    borderRadius: '8px', background: '#1B2A4A', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  lockedBanner: {
    display: 'flex', alignItems: 'center', gap: '8px', marginTop: '20px',
    padding: '12px 16px', background: '#F0FDF4', border: '1px solid #86EFAC',
    borderRadius: '8px', fontSize: '13px', color: '#16A34A', fontWeight: 500,
  },
};